// tz.js — DST-aware business-time helpers (single source of truth, frontend).
//
// Business timezone = Europe/Kyiv: EET (UTC+2) in winter, EEST (UTC+3) in summer,
// switching automatically on the EU DST schedule (01:00 UTC, last Sunday of
// March / October). "GMT+3" is the summer offset; it becomes GMT+2 in winter.
//
// This is the ESM twin of backend/utils/tz.cjs. Explicit instants stay
// byte-identical (backend/utils/tz.parity.test.js). The no-argument branch
// now deliberately diverges: ESM uses serverNow(), CJS uses Date.now().
//
// WHY this module exists: the frontend historically computed "business time" with
// hard-coded offsets — `Date.now() + 2*3600*1000` (GMT+2, utils.js) and
// `Date.now() + 3*3600*1000` (GMT+3, Invoice page). Fixed offsets cannot follow
// DST. This replaces them with one Intl-based, DST-correct implementation; no date
// library needed (Intl ships with every browser).
//
import { serverNow } from './server-clock.js';

// Every function accepts the instant as: epoch-ms number | Date | omitted (=> serverNow).
// Output is always the wall clock in Europe/Kyiv at that instant.

// Resolve the zone name once. Modern tzdata uses 'Europe/Kyiv'; older engines only
// know the legacy alias 'Europe/Kiev' (identical rules). Fail loud if neither
// resolves — never silently fall back to a wrong (UTC) clock.
export const TZ = (() => {
  for (const z of ['Europe/Kyiv', 'Europe/Kiev']) {
    try { new Intl.DateTimeFormat('en-CA', { timeZone: z }); return z; }
    catch { /* try next candidate */ }
  }
  throw new Error('[tz] No Europe/Kyiv timezone in this engine — cannot compute business time');
})();

// One reusable formatter. 'en-CA' + h23 yields stable 24h numeric parts
// regardless of the host locale.
const _DTF = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ, hourCycle: 'h23',
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit',
});

function _toMs(input) {
  // No-arg (and unparseable) → server estimate. Explicit instants unchanged.
  if (input == null) return serverNow();
  if (input instanceof Date) return input.getTime();
  const n = Number(input);
  return Number.isFinite(n) ? n : serverNow();
}

function _pad2(n) { return String(n).padStart(2, '0'); }

// Wall-clock components in Europe/Kyiv for the given instant.
export function parts(input) {
  const ms = _toMs(input);
  const o = {};
  for (const { type, value } of _DTF.formatToParts(new Date(ms))) {
    if (type !== 'literal') o[type] = value;
  }
  // hourCycle h23 can emit '24' for midnight on some engines — normalize to 0.
  return {
    year:   +o.year,
    month:  +o.month,
    day:    +o.day,
    hour:   (+o.hour) % 24,
    minute: +o.minute,
    second: +o.second,
  };
}

// Offset of Europe/Kyiv from UTC at the instant, in minutes (+120 winter, +180 summer).
export function offsetMinutes(input) {
  const ms = _toMs(input);
  const p = parts(ms);
  // Treat the Kyiv wall clock as if it were UTC, then measure the gap to the real
  // instant. The gap is the zone's offset for that instant (DST already applied).
  const asUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((asUTC - ms) / 60000);
}

// A Date whose UTC fields equal the Kyiv wall clock. Drop-in for legacy call sites
// that read `.getUTCHours()/.getUTCDate()/.toISOString().slice(0,10)` off a shifted
// Date. NOTE: do NOT do calendar arithmetic on this Date across a DST boundary —
// use dateKey()/minutesOfDay() instead (the offset is frozen at this instant).
export function zonedDate(input) {
  const ms = _toMs(input);
  return new Date(ms + offsetMinutes(ms) * 60000);
}

// 'YYYY-MM-DD' — the Kyiv calendar day. Replaces todayGMT2 / dateGMT2 / todayGMT3.
export function dateKey(input) {
  const p = parts(input);
  return `${p.year}-${_pad2(p.month)}-${_pad2(p.day)}`;
}

// 0..1439 — minutes since Kyiv midnight.
export function minutesOfDay(input) {
  const p = parts(input);
  return p.hour * 60 + p.minute;
}

// Day of week in Kyiv, 0=Sunday..6=Saturday (matches Date.getUTCDay semantics).
export function weekday(input) {
  return zonedDate(input).getUTCDay();
}

// 'DD/MM/YYYY' — replaces todayDisplayGMT2.
export function displayDate(input) {
  const p = parts(input);
  return `${_pad2(p.day)}/${_pad2(p.month)}/${p.year}`;
}

// 'DD/MM/YYYY, HH:MM:SS' — en-GB toLocaleString equivalent in Kyiv, not the browser TZ.
export function displayDateTime(input) {
  const p = parts(input);
  return `${_pad2(p.day)}/${_pad2(p.month)}/${p.year}, ${_pad2(p.hour)}:${_pad2(p.minute)}:${_pad2(p.second)}`;
}

// 'HH:MM' — replaces fmtTxTimeGMT2 / formatTimeGMT3.
export function fmtTime(input) {
  const p = parts(input);
  return `${_pad2(p.hour)}:${_pad2(p.minute)}`;
}

// 'DD.MM.YYYY HH:MM' — the format used in Telegram payment notifications.
export function fmtDateTime(input) {
  const p = parts(input);
  return `${_pad2(p.day)}.${_pad2(p.month)}.${p.year} ${_pad2(p.hour)}:${_pad2(p.minute)}`;
}

// 'GMT+2' | 'GMT+3' — human label for the active offset (notification footers, UI).
export function tzLabel(input) {
  const off = offsetMinutes(input);
  const sign = off >= 0 ? '+' : '-';
  const abs = Math.abs(off);
  const h = Math.floor(abs / 60), m = abs % 60;
  return m ? `GMT${sign}${h}:${_pad2(m)}` : `GMT${sign}${h}`;
}
